import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setRodmanFavicon, setDefaultFavicon } from './utils';
import ReactMarkdown from 'react-markdown';
import rodmanLogo from './rodman_logo.jpg';
import './Rodman.css';

// Import animation components
import { ThinkingIndicator, TypewriterText } from './components/RodmanAnimation';

const RodmanV2 = () => {
    const [messages, setMessages] = useState([]);
    const [contextSummary, setContextSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [typingMessageId, setTypingMessageId] = useState(null);
    const [stats, setStats] = useState(null);
    
    const messagesEndRef = useRef(null);
    const chatMessagesRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    // Handle URL parameters and state for direct analysis
    useEffect(() => {
        // Check for rich context from state (game analysis, standings, etc.)
        const gameContext = location.state?.gameContext;
        const standingsContext = location.state?.standingsContext;
        const gameString = location.state?.gameString;
        const league = location.state?.league;
        const presetQuery = location.state?.presetQuery;
        const autoStart = location.state?.autoStart;
        
        if (autoStart && presetQuery) {
            // Handle context-driven analysis
            let welcomeMessage;
            let contextData;
            
            if (gameContext) {
                welcomeMessage = `Alright, ${gameString}. Let me break this down for you.`;
                contextData = gameContext;
            } else if (standingsContext) {
                welcomeMessage = `Looking at the ${league} standings. Here's what I see...`;
                contextData = { 
                    standings: standingsContext, 
                    league,
                    focusTeam: location.state?.focusTeam 
                };
            } else {
                welcomeMessage = `Let me analyze this for you.`;
                contextData = { league };
            }
            
            setMessages([{
                id: Date.now(),
                type: 'rodman',
                content: welcomeMessage,
                timestamp: new Date(),
                sport: league?.toLowerCase() === 'nba' ? 'nba' : 'premier_league'
            }]);
            
            // Start analysis with V2 chat
            sendMessageV2(presetQuery, contextData);
            
            // Clean up state (stay on current route)
            navigate('/rodman', { replace: true });
        } else {
            // Load dynamic suggestions for clean start
            loadDynamicSuggestions();
        }
        
        // Set favicon
        setRodmanFavicon();
        return () => setDefaultFavicon();
    }, []);

    // Load stats on component mount
    useEffect(() => {
        const loadStats = async () => {
            try {
                const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'production' ? 'https://open-score-go-v1-a5ca8743fb39.herokuapp.com' : 'http://localhost:8000');
                const response = await fetch(`${apiBaseUrl}/api/v1/rodman/stats`);
                if (response.ok) {
                    const statsData = await response.json();
                    setStats(statsData);
                }
            } catch (error) {
                console.error('Error loading stats:', error);
            }
        };
        loadStats();
    }, []);

    // Smart scroll management
    useEffect(() => {
        const lastMessage = messages[messages.length - 1];
        if (!lastMessage) return;
        
        setTimeout(() => {
            if (lastMessage.type === 'user') {
                const userMessages = document.querySelectorAll('.message.user');
                const lastUserMessage = userMessages[userMessages.length - 1];
                if (lastUserMessage) {
                    lastUserMessage.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start',
                        inline: 'nearest'
                    });
                }
            } else if (lastMessage.type === 'rodman') {
                const rodmanMessages = document.querySelectorAll('.message.rodman');
                const lastRodmanMessage = rodmanMessages[rodmanMessages.length - 1];
                if (lastRodmanMessage) {
                    lastRodmanMessage.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start',
                        inline: 'nearest'
                    });
                }
            }
        }, 100);
    }, [messages]);

    const loadDynamicSuggestions = async () => {
        try {
            console.log('🎯 Loading dynamic suggestions...');
            
            const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : 'https://open-score-go-v1-a5ca8743fb39.herokuapp.com');
            const response = await fetch(`${apiBaseUrl}/api/v2/rodman/dynamic-suggestions?max_suggestions=4`);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            console.log('✅ Dynamic suggestions loaded:', data);
            
            setContextSummary(data.context_summary);
            
            // Create initial suggestions message (like original Rodman.js)
            if (messages.length === 0) {
                const suggestionMessage = {
                    id: Date.now(),
                    type: 'rodman',
                    content: "Try asking me about:",
                    timestamp: new Date(),
                    sport: 'general',
                    isSuggestions: true,
                    suggestionButtons: data.suggestions.map(s => s.text),
                    suggestions: data.suggestions,
                    onSuggestionClick: (suggestion, index) => {
                        const suggestionData = data.suggestions[index];
                        handleSuggestionClick(suggestionData);
                    }
                };
                
                setMessages([suggestionMessage]);
            }
            
        } catch (error) {
            console.error('❌ Error loading dynamic suggestions:', error);
            
            // Fallback suggestions message (like original Rodman.js)
            if (messages.length === 0) {
                const fallbackMessage = {
                    id: Date.now(),
                    type: 'rodman',
                    content: "Try asking me about:",
                    timestamp: new Date(),
                    sport: 'general',
                    isSuggestions: true,
                    suggestionButtons: [
                        "Show me your best accuracy spots",
                        "Which teams are surprising you this season?", 
                        "What's trending in the power rankings?"
                    ]
                };
                
                setMessages([fallbackMessage]);
            }
        }
    };

    const sendMessageV2 = async (messageText, gameContext = null, suggestionMetadata = null) => {
        if (!messageText.trim() || isLoading) return;

        // Create a unique request ID for this exact request
        const requestId = `${messageText}_${JSON.stringify(gameContext)}_${Date.now()}`;
        
        // Global duplicate prevention using a Set
        if (!window.rodmanActiveRequests) {
            window.rodmanActiveRequests = new Set();
        }
        
        // Check if this exact request is already being processed
        const requestKey = `${messageText}_${JSON.stringify(gameContext)}`;
        if (window.rodmanActiveRequests.has(requestKey)) {
            console.log('🚫 Preventing duplicate request already in progress:', requestKey);
            return;
        }
        
        // Mark this request as active
        window.rodmanActiveRequests.add(requestKey);
        
        // Enhanced duplicate message prevention
        if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            
            // Check if this is a duplicate user message
            if (lastMessage.content === messageText && lastMessage.type === 'user') {
                console.log('🚫 Preventing duplicate user message:', messageText);
                window.rodmanActiveRequests.delete(requestKey);
                return;
            }
            
            // Check if we just sent the same message (within last 3 seconds)
            const recentDuplicate = messages.slice(-3).find(msg => 
                msg.content === messageText && 
                msg.type === 'user' && 
                (Date.now() - msg.timestamp.getTime()) < 3000
            );
            
            if (recentDuplicate) {
                console.log('🚫 Preventing rapid duplicate message:', messageText);
                window.rodmanActiveRequests.delete(requestKey);
                return;
            }
        }

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: messageText,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);

        try {
            console.log('🚀 Sending V2 chat request:', { messageText, gameContext, suggestionMetadata });
            
            const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : 'https://open-score-go-v1-a5ca8743fb39.herokuapp.com');
            const response = await fetch(`${apiBaseUrl}/api/v1/rodman/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: messageText,
                    context: gameContext,
                    suggestion_metadata: suggestionMetadata
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            console.log('✅ V2 chat response:', data);
            
            const rodmanMessage = {
                id: Date.now() + 1,
                type: 'rodman',
                content: data.response,
                timestamp: new Date(),
                confidence: data.confidence,
                sport: data.sport_detected,
                responseTime: data.response_time_ms
            };

            setMessages(prev => [...prev, rodmanMessage]);
            setTypingMessageId(rodmanMessage.id);
            
            // Add follow-up suggestions as a message (like original Rodman.js)
            if (data.follow_up_suggestions && data.follow_up_suggestions.length > 0) {
                setTimeout(() => {
                    const suggestionMessage = {
                        id: Date.now() + 2,
                        type: 'rodman',
                        content: "Try asking me about:",
                        timestamp: new Date(),
                        sport: 'general',
                        isSuggestions: true,
                        suggestionButtons: data.follow_up_suggestions.map(s => s.text),
                        suggestions: data.follow_up_suggestions,
                        onSuggestionClick: (suggestion, index) => {
                            const suggestionData = data.follow_up_suggestions[index];
                            handleSuggestionClick(suggestionData);
                        }
                    };
                    
                    setMessages(prev => [...prev, suggestionMessage]);
                }, 1000); // Small delay like original
            }
            
        } catch (error) {
            console.error('❌ Error in V2 chat:', error);
            
            const errorMessage = {
                id: Date.now() + 1,
                type: 'rodman',
                content: "System hiccup. My accuracy stays money though - try again.",
                timestamp: new Date(),
                sport: 'general',
                isError: true
            };

            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
            // Clean up the active request tracking
            window.rodmanActiveRequests?.delete(requestKey);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        console.log('🎯 Suggestion clicked:', suggestion);
        
        // Send message with suggestion metadata
        sendMessageV2(suggestion.text, null, {
            query_type: suggestion.query_type,
            priority: suggestion.priority,
            league: suggestion.league,
            relevance_score: suggestion.relevance_score,
            metadata: suggestion.metadata
        });
    };

    const getSportIcon = (sport) => {
        switch(sport) {
            case 'nba': return '🏀';
            case 'premier_league': return '⚽';
            default: return '⚡';
        }
    };

    const formatConfidence = (confidence) => {
        if (!confidence) return null;
        const percentage = Math.round(confidence * 100);
        
        if (percentage >= 75) {
            return { level: 'high', text: `${percentage}% confident`, color: '#34C759' };
        } else if (percentage >= 60) {
            return { level: 'medium', text: `${percentage}% confident`, color: '#FF9F0A' };
        } else {
            return { level: 'low', text: `${percentage}% confident`, color: '#8E8E93' };
        }
    };

    return (
        <div className="rodman-container">
            {/* Header with Context */}
            <div className="rodman-header">
                <div className="rodman-avatar">
                    <img src={rodmanLogo} alt="Rodman" className="avatar-image" />
                </div>
                <div className="rodman-info">
                    <h1 className="rodman-title">RODMAN AI</h1>
                    <p className="rodman-subtitle">
                        {stats?.full_description || 'Loading real-time accuracy data...'}
                    </p>
                </div>
                
            </div>

            {/* Chat Messages */}
            <div className="chat-messages" ref={chatMessagesRef}>
                {messages.map((message, index) => {
                    const isLatest = index === messages.length - 1;
                    const isTyping = typingMessageId === message.id;
                    
                    return (
                        <div key={message.id} className={`message ${message.type}`}>
                            <div className={`message-content ${message.isSuggestions ? 'isSuggestions' : ''}`}>
                                <div className="message-text">
                                    {message.type === 'rodman' && isTyping ? (
                                        <TypewriterText
                                            text={message.content}
                                            speed={20}
                                            onComplete={() => setTypingMessageId(null)}
                                            isMarkdown={true}
                                            markdownComponents={{
                                                p: ({ children }) => <span>{children}</span>,
                                                strong: ({ children }) => <strong style={{ fontWeight: '700', color: '#007AFF', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>{children}</strong>,
                                                em: ({ children }) => <em style={{ fontStyle: 'italic', color: '#007AFF', opacity: '0.9' }}>{children}</em>
                                            }}
                                        />
                                    ) : message.type === 'rodman' ? (
                                        <ReactMarkdown 
                                            components={{
                                                p: ({ children }) => <span>{children}</span>,
                                                strong: ({ children }) => <strong style={{ fontWeight: '700', color: '#007AFF', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>{children}</strong>,
                                                em: ({ children }) => <em style={{ fontStyle: 'italic', color: '#007AFF', opacity: '0.9' }}>{children}</em>
                                            }}
                                        >
                                            {message.content}
                                        </ReactMarkdown>
                                    ) : (
                                        message.content
                                    )}
                                </div>

                                {message.suggestionButtons && (
                                    <div className="suggestion-buttons">
                                        {message.suggestionButtons.map((suggestion, index) => (
                                            <button
                                                key={index}
                                                className="suggestion-btn"
                                                onClick={() => {
                                                    if (message.onSuggestionClick) {
                                                        message.onSuggestionClick(suggestion, index);
                                                    } else {
                                                        // Fallback for simple suggestion
                                                        sendMessageV2(suggestion, null, null);
                                                    }
                                                }}
                                            >
                                                {suggestion}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            
                            {message.confidence && (
                                <div 
                                    className="confidence-indicator"
                                    style={{ color: formatConfidence(message.confidence).color }}
                                >
                                    🎯 {formatConfidence(message.confidence).text}
                                </div>
                            )}
                            
                            {message.responseTime && (
                                <div className="response-time">
                                    ⚡ {message.responseTime}ms
                                </div>
                            )}
                            
                            <div className="message-time">
                                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>
                        </div>
                    );
                })}
                
                {isLoading && (
                    <div className="message rodman">
                        <div className="message-content">
                            <ThinkingIndicator text="Analyzing" />
                        </div>
                    </div>
                )}
                
                <div ref={messagesEndRef} />
            </div>

            {/* Dynamic Suggestions - Using Original Styling */}

        </div>
    );
};

export default RodmanV2;
